import React from 'react'

/**
 * Placement of the tooltip relative to its trigger element.
 *
 * `BOTTOM` is the ADEL default and maps to the bare `Tooltip--align*`
 * modifiers (no position prefix). All other values map to the
 * `Tooltip--position<Placement>--align*` modifier family.
 */
export enum TooltipPosition {
  BOTTOM = 'bottom',
  TOP = 'top',
  RIGHT = 'right',
  LEFT = 'left'
}

/**
 * Alignment of the tooltip along the edge described by {@link TooltipPosition}.
 * Maps to the ADEL `--alignStart | --alignCenter | --alignEnd` modifiers.
 */
export enum TooltipAlign {
  START = 'start',
  CENTER = 'center',
  END = 'end'
}

/**
 * Visual size of the tooltip.
 *
 * `SMALL` applies the `Tooltip--small` modifier and is intended for short,
 * single-line labels on Icon Button, Avatar, and Toggle Icon Button targets.
 * Per ADEL, the small tooltip always renders bottom-centered.
 */
export enum TooltipSize {
  DEFAULT = 'default',
  SMALL = 'small'
}

/**
 * Shape of the trigger/target element that owns the tooltip.
 *
 * `TEXT` renders the ADEL `Tooltip-target` treatment (dashed underline).
 * `ICON` renders the `Tooltip-icon-target` treatment used for icons and
 * icon buttons.
 */
export enum TooltipTargetVariant {
  TEXT = 'text',
  ICON = 'icon'
}

export type TooltipProps = {
  /** Trigger content the user hovers, focuses, or touches. */
  children: React.ReactNode
  /** Text/flow content rendered inside the tooltip overlay. */
  content: React.ReactNode
  /** Trigger treatment. Defaults to {@link TooltipTargetVariant.TEXT}. */
  targetVariant?: TooltipTargetVariant
  /**
   * Tooltip placement relative to the target. Defaults to bottom.
   * Ignored when `size` is `small`, which always renders bottom-centered.
   */
  position?: TooltipPosition
  /**
   * Tooltip alignment along the placement edge. Defaults to start.
   * Ignored when `size` is `small`, which always renders bottom-centered.
   */
  align?: TooltipAlign
  /**
   * Tooltip size. Defaults to {@link TooltipSize.DEFAULT}. The `small` size is
   * always bottom-centered, so `position` and `align` have no effect with it.
   */
  size?: TooltipSize
  /**
   * Whether to associate the tooltip with the target via `aria-labelledby`
   * instead of the default `aria-describedby`. Choose based on whether the
   * tooltip is the accessible name (label) or supplementary description.
   */
  useLabelledBy?: boolean
  /**
   * Extra class names appended to the target element. Use for utility spacing
   * classes such as `u-marginLeft4` on icon targets.
   */
  className?: string
  /** Optional test id root applied to the target, tooltip, and content. */
  testId?: string
}
